import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../auth/useAuth'
import { PageScrollArea, PageView } from '../components/ui/PageScrollArea'
import { pagePath } from '../lib/routes'
import { PageIcon } from '../components/PageIcon'

// Grouped the same way as the left-nav (Manage/Control/Observe) so Home
// reads as a map of the application rather than a second, differently
// ordered list of it — see AppShell.tsx's NAV_GROUPS and the prototype's own
// comment on this grouping.
const GROUPS = [
  {
    labelKey: 'appShell.nav.manage',
    items: [
      {
        page: 'subscription',
        titleKey: 'home.launchers.subscription.title',
        descKey: 'home.launchers.subscription.desc',
      },
      {
        page: 'additional-volume',
        titleKey: 'home.launchers.additionalVolume.title',
        descKey: 'home.launchers.additionalVolume.desc',
      },
      {
        page: 'excess-volume',
        titleKey: 'home.launchers.excessVolume.title',
        descKey: 'home.launchers.excessVolume.desc',
      },
    ],
  },
  {
    labelKey: 'appShell.nav.control',
    items: [
      {
        page: 'user-limits',
        titleKey: 'home.launchers.userLimits.title',
        descKey: 'home.launchers.userLimits.desc',
      },
      {
        page: 'monthly-limits',
        titleKey: 'home.launchers.monthlyLimits.title',
        descKey: 'home.launchers.monthlyLimits.desc',
        preview: true,
      },
      {
        page: 'tenant-limits',
        titleKey: 'home.launchers.tenantLimits.title',
        descKey: 'home.launchers.tenantLimits.desc',
        preview: true,
      },
    ],
  },
  {
    labelKey: 'appShell.nav.observe',
    items: [
      {
        page: 'usage',
        titleKey: 'home.launchers.usage.title',
        descKey: 'home.launchers.usage.desc',
      },
    ],
  },
] as const

function timeOfDay(): 'morning' | 'afternoon' | 'evening' {
  const hour = new Date().getHours()
  if (hour < 12) return 'morning'
  if (hour < 18) return 'afternoon'
  return 'evening'
}

export function Home() {
  const { t } = useTranslation()
  const { userName, userEmail } = useAuth()
  const navigate = useNavigate()
  const firstName = (userName ?? userEmail ?? '').split(/[\s@]/)[0]
  const greeting = t(`home.timeGreeting.${timeOfDay()}`)

  // The prototype's `.home-view`: the greeting and caption are a fixed
  // header, and the page cards scroll below it (`.home-columns`). The cards
  // span the full content width — three to a row, two below 980px. The
  // notification centre that used to share the page is gone; the top-bar
  // menu is the only notification surface (MS-996).
  return (
    <PageView>
      {/* `.home-header`: 48px from the top bar and 40px from the nav, which is
          24px and 8px more than the shell's own padding gives. */}
      <div className="shrink-0 pt-6 px-2">
        <div className="text-[24px] leading-[1.5] font-semibold mb-1">
          {firstName ? t('home.greetingWithName', { greeting, name: firstName }) : greeting}
        </div>
        <div className="text-[14px] leading-[1.5] text-[var(--text-secondary)] mb-8">{t('home.subtitle')}</div>
      </div>

      {/* `.home-columns`: `overflow-y: auto` and a 0 40px 40px padding — 40px
          from the nav and from the window edge — with no reserved gutter,
          unlike the other pages' `.tab-scroll-area`. */}
      <PageScrollArea stableGutter={false} className="pl-2 !pr-10 !pb-10">
        {GROUPS.map((group, i) => (
          <div key={group.labelKey} className={i > 0 ? 'mt-7' : undefined}>
            <div className="text-[11px] font-semibold uppercase tracking-[0.8px] text-[var(--text-tertiary)] mb-2.5">
              {t(group.labelKey)}
            </div>
            <div data-testid="home-grid" className="grid grid-cols-2 min-[981px]:grid-cols-3 gap-4">
              {group.items.map((l) => (
                // `.home-card` (and `.coming-soon` for a preview page): a top-aligned
                // column with 12px between icon, title and caption. The dashed
                // teal-300 border and the teal-100/800 badge are the prototype's
                // fixed teal steps, the same in both themes.
                <button
                  key={l.page}
                  onClick={() => { navigate(pagePath(l.page)) }}
                  className={`relative flex flex-col justify-start gap-3 text-left border rounded-xl p-6 bg-[var(--bg-card)] hover:border-[var(--accent)] hover:shadow-[var(--shadow-md)] transition-all ${
                    'preview' in l && l.preview ? 'border-dashed border-[var(--teal-300)]' : 'border-[var(--border)]'
                  }`}
                >
                  {'preview' in l && l.preview && (
                    <span className="absolute top-3 right-3 text-[10px] font-semibold px-2 py-0.5 rounded-[10px] bg-[var(--teal-100)] text-[var(--teal-800)]">
                      {t('common.previewBadge')}
                    </span>
                  )}
                  <div className="w-10 h-10 rounded-[10px] bg-[var(--accent-subtle)] flex items-center justify-center">
                    <PageIcon page={l.page} size={20} stroke="var(--accent)" />
                  </div>
                  <div className="text-[14px] font-semibold">{t(l.titleKey)}</div>
                  <div className="text-[12px] text-[var(--text-secondary)] leading-[1.5]">{t(l.descKey)}</div>
                </button>
              ))}
            </div>
          </div>
        ))}
      </PageScrollArea>
    </PageView>
  )
}
